import { Helmet } from 'react-helmet-async'
import { FileText, ShoppingBag, User, CreditCard, Shield, AlertCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const DistanceSales = () => {
  const { language } = useLanguage()

  const content = {
    tr: {
      title: 'Mesafeli Satış Sözleşmesi',
      lastUpdated: 'Son Güncelleme: 6 Kasım 2024',
      sections: [
        {
          icon: ShoppingBag,
          title: 'Sözleşmenin Konusu',
          content: `Bu Mesafeli Satış Sözleşmesi (bundan sonra "Sözleşme" olarak anılacaktır), Thinklyze Bilgi Teknolojileri Limited Şirketi (bundan sonra "SATICI" olarak anılacaktır) ile web sitesi üzerinden dijital ürün veya hizmet satın alan müşteri (bundan sonra "ALICI" olarak anılacaktır) arasındaki satış ve teslim koşullarını düzenlemektedir.

Bu Sözleşme, 6502 sayılı Tüketicinin Korunması Hakkında Kanun, Mesafeli Sözleşmeler Yönetmeliği ve ilgili mevzuat hükümlerine tabidir.`,
        },
        {
          icon: User,
          title: 'Taraflar',
          content: `SATICI:
Thinklyze Bilgi Teknolojileri Limited Şirketi
Adres: Zeytinlik Mah. Fişekhane Cad. Carousel No: 5 İç Kapı No: 17 Bakırköy/İstanbul
Telefon: 0553 657 64 54
E-posta: info@thinklyze.com
Web: www.thinklyze.com

ALICI:
Web sitesi üzerinden dijital ürün veya hizmet satın alan gerçek veya tüzel kişi.`,
        },
        {
          icon: FileText,
          title: 'Sipariş ve Sözleşme Süreci',
          content: `1. ALICI, web sitesinde yer alan ürün veya hizmeti seçer ve sipariş verir.
2. ALICI, sipariş formunu doldurarak ödeme bilgilerini girer.
3. Ödeme işlemi tamamlandıktan sonra ALICI'ya sipariş onay e-postası gönderilir.
4. Sipariş onay e-postası ile birlikte bu Mesafeli Satış Sözleşmesi de ALICI'ya iletilecektir.
5. Sipariş onay e-postası ile Sözleşme, ALICI'ya ulaştığı anda sözleşme kurulmuş sayılır.`,
        },
        {
          icon: CreditCard,
          title: 'Fiyat ve Ödeme',
          content: `• Ürün fiyatları, KDV dahil olarak TL cinsinden belirtilmiştir.
• Ödeme, kredi kartı, banka kartı veya PayTR/İyzico ödeme sistemleri üzerinden yapılabilir.
• Ödeme işlemi, 3D Secure güvenlik sistemi ile korunmaktadır.
• ALICI, sipariş vermekle ürün bedelini ödemeyi kabul etmiş sayılır.
• Ödeme işlemi tamamlandığında ALICI'ya fatura düzenlenir ve e-posta ile gönderilir.`,
        },
        {
          icon: Shield,
          title: 'Teslimat',
          content: `Dijital ürünler ve online eğitimler anında teslim edilir:

• Satın alma işleminizden hemen sonra erişim bilgileri e-posta ile gönderilir
• Hesabınıza giriş yaparak ürünlere erişebilirsiniz
• Video eğitimler ve dijital içerikler anında erişilebilir durumda olur
• Teslimat süresi en geç 24 saat içinde tamamlanır`,
        },
        {
          icon: AlertCircle,
          title: 'Cayma Hakkı',
          content: `6502 sayılı Tüketicinin Korunması Hakkında Kanun'un 15/g maddesi uyarınca, tüketicinin onayı ile anında ifa edilen veya hizmet unsuru ağırlıklı olan dijital içerikler için cayma hakkı uygulanmamaktadır.

Ancak, aşağıdaki durumlarda cayma hakkı saklıdır:

• Ürün veya hizmet teknik olarak erişilebilir değilse
• Satın aldığınız ürün ile teslim edilen ürün farklıysa
• Ürün açıklamasında belirtilen özelliklere sahip değilse

Cayma hakkını kullanmak istediğinizde, info@thinklyze.com adresine yazılı olarak başvurmanız gerekmektedir.`,
        },
        {
          icon: FileText,
          title: 'Genel Hükümler',
          content: `• Bu Sözleşme, ALICI'nın sipariş vermesi ve ödeme yapması ile birlikte geçerli hale gelir.
• ALICI, satın aldığı ürün veya hizmeti sadece kişisel kullanım amacıyla kullanabilir.
• Ürün veya hizmetin üçüncü kişilerle paylaşılması, kopyalanması veya ticari amaçla kullanılması yasaktır.
• SATICI, teknik sorunlar nedeniyle hizmette geçici aksama yaşanması durumunda sorumluluk kabul etmez.
• Bu Sözleşme'de belirtilmeyen konularda Türk Ticaret Kanunu, Borçlar Kanunu ve ilgili mevzuat hükümleri uygulanır.`,
        },
      ],
    },
    en: {
      title: 'Distance Sales Contract',
      lastUpdated: 'Last Updated: November 6, 2024',
      sections: [
        {
          icon: ShoppingBag,
          title: 'Subject of the Contract',
          content: `This Distance Sales Contract (hereinafter referred to as "Contract") regulates the sales and delivery conditions between Thinklyze Information Technologies Limited Company (hereinafter referred to as "SELLER") and the customer who purchases digital products or services through the website (hereinafter referred to as "BUYER").

This Contract is subject to Law No. 6502 on the Protection of Consumers, the Distance Contracts Regulation and relevant legislation provisions.`,
        },
        {
          icon: User,
          title: 'Parties',
          content: `SELLER:
Thinklyze Information Technologies Limited Company
Address: Zeytinlik Mah. Fişekhane Cad. Carousel No: 5 İç Kapı No: 17 Bakırköy/İstanbul
Phone: 0553 657 64 54
Email: info@thinklyze.com
Web: www.thinklyze.com

BUYER:
Real or legal person who purchases digital products or services through the website.`,
        },
        {
          icon: FileText,
          title: 'Order and Contract Process',
          content: `1. BUYER selects the product or service on the website and places an order.
2. BUYER fills in the order form and enters payment information.
3. After the payment is completed, an order confirmation email is sent to the BUYER.
4. This Distance Sales Contract will also be sent to the BUYER along with the order confirmation email.
5. The contract is deemed to have been concluded when the order confirmation email and Contract reach the BUYER.`,
        },
        {
          icon: CreditCard,
          title: 'Price and Payment',
          content: `• Product prices are specified in TL including VAT.
• Payment can be made via credit card, bank card or PayTR/İyzico payment systems.
• Payment transactions are protected by 3D Secure security system.
• BUYER is deemed to have accepted to pay the product price by placing an order.
• An invoice is issued and sent to the BUYER by email when the payment is completed.`,
        },
        {
          icon: Shield,
          title: 'Delivery',
          content: `Digital products and online training are delivered instantly:

• Access information is sent by email immediately after your purchase
• You can access products by logging into your account
• Video training and digital content become instantly accessible
• Delivery is completed within 24 hours at the latest`,
        },
        {
          icon: AlertCircle,
          title: 'Right of Withdrawal',
          content: `In accordance with Article 15/g of Law No. 6502 on the Protection of Consumers, the right of withdrawal does not apply to digital content that is fulfilled instantly with the consumer's consent or where the service element is predominant.

However, the right of withdrawal is reserved in the following cases:

• If the product or service is technically inaccessible
• If the product delivered differs from the one purchased
• If the product does not have the features specified in the product description

To exercise your right of withdrawal, you must apply in writing to info@thinklyze.com.`,
        },
        {
          icon: FileText,
          title: 'General Provisions',
          content: `• This Contract becomes effective with the BUYER placing an order and making payment.
• BUYER can use the purchased product or service only for personal use purposes.
• Sharing, copying or commercial use of the product or service with third parties is prohibited.
• SELLER does not accept responsibility in case of temporary interruption in service due to technical problems.
• Turkish Commercial Code, Code of Obligations and relevant legislation provisions apply to matters not specified in this Contract.`,
        },
      ],
    },
  }

  const pageContent = content[language as 'tr' | 'en' | 'ru'] || content['en']

  return (
    <>
      <Helmet>
        <title>{pageContent.title} - Thinklyze</title>
        <meta name="description" content={language === 'tr' ? 'Mesafeli satış sözleşmesi. Dijital ürünler ve online eğitimler için mesafeli satış koşulları.' : 'Distance sales contract. Distance sales conditions for digital products and online training.'} />
        <link rel="canonical" href="https://thinklyze.com/distance-sales" />
        <link rel="alternate" hreflang="tr" href="https://thinklyze.com/distance-sales" />
        <link rel="alternate" hreflang="en" href="https://thinklyze.com/distance-sales?lang=en" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pt-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {pageContent.title}
            </h1>
            <p className="text-gray-600">{pageContent.lastUpdated}</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {pageContent.sections.map((section, index) => {
              const Icon = section.icon
              return (
                <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 pt-2">
                      {section.title}
                    </h2>
                  </div>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line ml-16">
                    {section.content}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Contact Info */}
          <div className="mt-12 bg-blue-50 rounded-xl p-8 border border-blue-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'İletişim' : 'Contact'}
            </h3>
            <p className="text-gray-700 mb-4">
              {language === 'tr'
                ? 'Sözleşme ile ilgili sorularınız için:'
                : 'For questions about the contract:'}
            </p>
            <p className="text-gray-900 font-medium">info@thinklyze.com</p>
            <p className="text-gray-900 font-medium mt-2">0553 657 64 54</p>
          </div>
        </div>
      </div>
    </>
  )
}

export default DistanceSales















